import type { AttendeeWithTile } from './useAudioVideo/types'
import { TileUtils } from './useAudioVideo/utils'

interface Props {
  listContainer: Ref<HTMLElement | null>
  participantTiles: Ref<AttendeeWithTile[]>
  setTileLimit(limit: number | undefined): void
  getTileWidth(): number | null
  getGapWidth(): number | null
}

interface UseTileRowSpace {
  hiddenTiles: Ref<AttendeeWithTile[]>
}

export const TILE_INSET = 12

export function useTileRowSpace({ listContainer, participantTiles, setTileLimit, getTileWidth, getGapWidth }: Props): UseTileRowSpace {
  const hiddenTiles = computed(() => participantTiles.value.filter(TileUtils.isLimited))
  let tileLimit: number | undefined

  const observer = new ResizeObserver(([entry]) => {
    _calcNumTiles(entry.contentRect.width)
  })

  let domElementInitiated = false
  watch(
    listContainer,
    element => {
      if (element) {
        if (domElementInitiated) return
        observer.observe(element)
        // getBoundingClientRect returns a border-box, but it's fine because border+padding = 0
        _calcNumTiles(element.getBoundingClientRect().width)
        domElementInitiated = true
      } else {
        domElementInitiated = false
        tileLimit = undefined
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    observer.disconnect()
  })

  // This watcher is to deal with 1 specific case where an attendee leaves and only 1 hidden
  // attendee remains. In this case, since no resize event occurred, we manually trigger the
  // recalculation.
  //
  // The reverse (attendee joining) should not be checked here because this triggers before
  // a DOM update occurs, so it will see 1 hidden attendee but without a hidden attendee tile.
  watch(
    () => hiddenTiles.value.length,
    (length, oldLength) => {
      if (listContainer.value && length === 1 && oldLength > 1) {
        _calcNumTiles(listContainer.value.getBoundingClientRect().width)
      }
    }
  )

  function _calcNumTiles(width: number) {
    const tileWidth = getTileWidth()
    const gap = getGapWidth()

    if (!width || !tileWidth || !gap) return

    const numTiles = Math.floor((width + gap) / (tileWidth + gap))
    const newLimit = _accountForSingleHiddenAttendee(numTiles)

    if (newLimit !== tileLimit) {
      setTileLimit((tileLimit = newLimit))
    }
  }

  function _accountForSingleHiddenAttendee(numTiles: number) {
    // It's easy to get turned around in here. numTiles is part of the to-state.
    // hiddenTiles and participantTiles are part of the from-state.
    // Keep that in mind.

    // hidden attendees tile will be deactivated - pre-account for the space it will leave
    if (numTiles >= participantTiles.value.length - 1 && hiddenTiles.value.length > 0) {
      return numTiles + 1
    }
    // hidden attendees tile will be activated — pre-account for the space it will take
    if (numTiles < participantTiles.value.length && hiddenTiles.value.length === 0) {
      return numTiles - 1
    }
    return numTiles
  }

  return { hiddenTiles }
}
